import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { LegalAcceptanceGate } from '@/app/components/legal-acceptance-gate';

// Routes a staff user without an org still needs to reach before one exists.
const PRE_ORG_ROUTES = ['/onboarding'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipLegalGate?: boolean;
}

export function ProtectedRoute({
  children,
  skipLegalGate = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Staff/superusers without an org cannot access org-scoped pages; route
  // them to the platform admin console instead. Allowlisted routes (e.g. the
  // onboarding wizard) must still be reachable before an org is created.
  if (
    user?.is_staff &&
    !user?.organization &&
    !PRE_ORG_ROUTES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
  ) {
    return <Navigate to="/platform-admin" replace />;
  }

  return skipLegalGate ? (
    <>{children}</>
  ) : (
    <LegalAcceptanceGate>{children}</LegalAcceptanceGate>
  );
}
